// Angular Files
import {
 DOCUMENT, Injectable, InjectionToken, inject, signal
} from '@angular/core';

// Teller Online Files
import { RumEvent } from '../rum/rum.models';
import { TellerOnlineRumService } from '../rum/rum.service';

// Component Files
import { SURVEYS } from './engagement.config';
import {
    MAX_SURVEY_COMMENT_LENGTH,
    SurveyDefinition
} from './engagement.models';
import { isPageBusy } from './engagement-context';
import { TellerOnlineGuideService } from './guide.service';
import { readStored, removeStored, writeStored } from './engagement-storage';

export const SURVEY_DEFINITIONS = new InjectionToken<SurveyDefinition[]>('SURVEY_DEFINITIONS', {
    factory: () => SURVEYS
});

const STATE_PREFIX = 'to-survey:';
const PAGE_VIEWS_KEY = 'to-survey-page-views';
/** A survey that finds the page busy tries again a few times, then waits for the next trigger. */
const BUSY_RETRY_MS = 8000;
const BUSY_RETRIES = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

interface SurveyState {
    version: number;
    answered: boolean;
    dismissals: number;
    lastDismissedAt: number;
}

/**
 * Runs in-app surveys. Frequency capping lives in localStorage: an answered survey never returns
 * (until its version changes), a dismissed one waits out its cooldown and gives up after
 * `maxDismissals`. Answers leave only as RUM `survey_submitted` events.
 */
@Injectable({ providedIn: 'root' })
export class TellerOnlineSurveyService {
    private _current = signal<SurveyDefinition | null>(null);

    readonly current = this._current.asReadonly();

    private _submitted = signal(false);

    /** True after an answer is sent, while the thank-you is on screen. */
    readonly submitted = this._submitted.asReadonly();

    private _definitions = inject(SURVEY_DEFINITIONS);

    private _rum = inject(TellerOnlineRumService);

    private _guides = inject(TellerOnlineGuideService);

    private _document = inject(DOCUMENT);

    private _shownThisSession = new Set<string>();

    public get definitions(): SurveyDefinition[] {
        return this._definitions;
    }

    public isEligible(survey: SurveyDefinition, now = Date.now()): boolean {
        const state = this._state(survey);
        if (state.answered || state.dismissals >= survey.maxDismissals) return false;

        return state.dismissals === 0
            || now - state.lastDismissedAt >= survey.cooldownDays * DAY_MS;
    }

    /** Surveys of this portal waiting on a named event, e.g. a successful payment. */
    public triggerEvent(name: string, portal: SurveyDefinition['portal']): void {
        this._definitions
            .filter(s => s.portal === portal && s.trigger.type === 'event'
                && s.trigger.name === name)
            .forEach(s => this._schedule(s));
    }

    /** Counts a page view and offers any survey whose threshold is now reached. */
    public countPageView(portal: SurveyDefinition['portal']): void {
        const count = (readStored<number>(PAGE_VIEWS_KEY) ?? 0) + 1;
        writeStored(PAGE_VIEWS_KEY, count);

        this._definitions
            .filter(s => s.portal === portal && s.trigger.type === 'pageViews'
                && count >= s.trigger.count)
            .forEach(s => this._schedule(s));
    }

    /** Shows a survey now, ignoring frequency capping; for demos. */
    public force(id: string): boolean {
        const survey = this._definitions.find(s => s.id === id);
        if (!survey) return false;

        this._present(survey, true);

        return this._current() === survey;
    }

    public submit(score: number, comment: string): void {
        const survey = this._current();
        if (!survey || this._submitted()) return;

        const text = comment.trim().slice(0, MAX_SURVEY_COMMENT_LENGTH);
        this._rum.recordEvent(RumEvent.surveySubmitted, {
            surveyId: survey.id,
            score,
            scale: survey.scale,
            hasComment: text.length > 0,
            comment: text
        });
        this._save(survey, { answered: true });
        this._submitted.set(true);
    }

    /** Removes the card once the thank-you has been seen. */
    public close(): void {
        this._current.set(null);
        this._submitted.set(false);
    }

    public dismiss(): void {
        const survey = this._current();
        if (!survey || this._submitted()) return;

        this._rum.recordEvent(RumEvent.surveyDismissed, { surveyId: survey.id });
        this._save(survey, {
            dismissals: this._state(survey).dismissals + 1,
            lastDismissedAt: Date.now()
        });
        this.close();
    }

    /** Forgets this browser's answers, dismissals and page-view count. */
    public reset(id?: string): void {
        removeStored(STATE_PREFIX + (id ?? ''));
        if (!id) removeStored(PAGE_VIEWS_KEY);
        this._shownThisSession.clear();
    }

    private _schedule(survey: SurveyDefinition): void {
        if (this._shownThisSession.has(survey.id) || !this.isEligible(survey)) return;

        setTimeout(() => this._tryPresent(survey, BUSY_RETRIES), survey.delayMs);
    }

    private _tryPresent(survey: SurveyDefinition, retries: number): void {
        if (!isPageBusy(this._document)) {
            this._present(survey, false);
        } else if (retries > 0) {
            setTimeout(() => this._tryPresent(survey, retries - 1), BUSY_RETRY_MS);
        }
    }

    private _present(survey: SurveyDefinition, force: boolean): void {
        if (this._current() || this._guides.active()) return;
        if (!force && (this._shownThisSession.has(survey.id) || !this.isEligible(survey))) return;

        this._shownThisSession.add(survey.id);
        this._current.set(survey);
        this._rum.recordEvent(RumEvent.surveyShown, { surveyId: survey.id });
    }

    private _state(survey: SurveyDefinition): SurveyState {
        const stored = readStored<SurveyState>(STATE_PREFIX + survey.id);

        return stored?.version === survey.version
            ? stored
            : {
 version: survey.version, answered: false, dismissals: 0, lastDismissedAt: 0
};
    }

    private _save(survey: SurveyDefinition, change: Partial<SurveyState>): void {
        writeStored(STATE_PREFIX + survey.id, { ...this._state(survey), ...change });
    }
}
